/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// API utility
import { api } from "utils/api";
// Department utilities
import { getDepartmentLabel } from "utils/departmentUtils";
// Ticket access utilities
import { hasTicketAccess } from "utils/ticketAccessUtils";
import { logTicketEvent } from "utils/ticketLogger";
import { formatDbDateTime } from "utils/dateFormatter";

/**
 * Map priority level to label and color
 * @param {number|string} level - Priority level (1-5)
 * @returns {Object} Object with label, color, bgColor and textColor
 */
const getPriorityInfo = (level) => {
  const levelNum = parseInt(level);
  switch (levelNum) {
    case 1:
      return { label: "Urgent", color: "error", bgColor: "#FEE2E2", textColor: "#B91C1C" };
    case 2:
      return { label: "High", color: "warning", bgColor: "#FEF3C7", textColor: "#D97706" };
    case 3:
      return { label: "Medium", color: "info", bgColor: "#E0F2FE", textColor: "#0369A1" };
    case 4:
      return { label: "Low", color: "success", bgColor: "#DCFCE7", textColor: "#15803D" };
    case 5:
      return { label: "Minor", color: "secondary", bgColor: "#F3E8FF", textColor: "#7E22CE" };
    default:
      return { label: "Unknown", color: "default", bgColor: "#F3F4F6", textColor: "#6B7280" };
  }
};

/**
 * Get status info based on resolved status
 * @param {string} resolved - Resolved status ('yes', 'dnc', or empty)
 * @returns {Object} Object with label, color, bgColor and textColor
 */
const getStatusInfo = (resolved) => {
  const resolvedStatus = (resolved || "").toLowerCase();

  if (resolvedStatus === "yes") {
    return { label: "Resolved", color: "success", bgColor: "#DCFCE7", textColor: "#15803D" };
  } else if (resolvedStatus === "dnc") {
    return { label: "Done", color: "warning", bgColor: "#FEF3C7", textColor: "#D97706" };
  } else {
    return { label: "Open", color: "error", bgColor: "#FEE2E2", textColor: "#B91C1C" };
  }
};

/**
 * Format ticket data from API response
 * @param {Object} ticketData - Raw ticket data from API
 * @param {string} customerName - Optional customer name to display
 * @returns {Object} Formatted ticket object
 */
const formatTicketData = (ticketData, customerName = null) => {
  return {
    id: ticketData.ticket_id,
    code: ticketData.ticket_code,
    customerId: ticketData.customer_id,
    customerName: customerName || `Customer ID: ${ticketData.customer_id}`,
    createUser: ticketData.create_user,
    currentUser: ticketData.current_user || "-",
    createDatetime: formatDbDateTime(ticketData.create_datetime),
    updateDatetime: formatDbDateTime(ticketData.update_datetime),
    resolveDatetime: formatDbDateTime(ticketData.resolve_datetime),
    status: getStatusInfo(ticketData.resolved),
    department: ticketData.department,
    departmentLabel: getDepartmentLabel(ticketData.department),
    level: ticketData.level,
    priority: getPriorityInfo(ticketData.level),
  };
};

/**
 * Format message data from API response
 * @param {Object} messageData - Raw message data from API
 * @returns {Object} Formatted message object
 */
const formatMessageData = (messageData) => {
  return {
    id: messageData.ticket_message_id,
    datetime: formatDbDateTime(messageData.datetime),
    user: messageData.user,
    message: messageData.message,
  };
};

/**
 * Fetch ticket details by ID
 * @param {number} ticketId - Ticket ID to fetch
 * @param {string} customerName - Optional customer name for display
 * @returns {Promise<Object>} Promise resolving to ticket data and error
 */
export const fetchTicketById = async (ticketId, customerName) => {
  try {
    // Fetch ticket details
    const response = await api.post("/db/tickets/filter", {
      filters: { ticket_id: ticketId },
      columns: [
        "ticket_id",
        "ticket_code",
        "customer_id",
        "create_user",
        "current_user", // Make sure we're requesting the current_user field
        "create_datetime",
        "update_datetime",
        "resolve_datetime",
        "resolved",
        "department",
        "level",
      ],
    });

    if (!Array.isArray(response) || response.length === 0) {
      return { error: "Ticket not found" };
    }

    const ticketData = response[0];

    // Log the raw ticket data for debugging
    console.log("Raw ticket data:", ticketData);

    // Format the ticket data
    const formattedTicket = {
      id: ticketData.ticket_id,
      code: ticketData.ticket_code,
      customerId: ticketData.customer_id,
      customerName: customerName,
      createUser: ticketData.create_user,
      currentUser: ticketData.current_user, // Ensure we're passing through the current_user
      createDatetime: ticketData.create_datetime,
      updateDatetime: ticketData.update_datetime,
      resolveDatetime: ticketData.resolve_datetime,
      resolved: ticketData.resolved,
      department: ticketData.department,
      level: ticketData.level,
      // Add status object
      status: {
        label:
          ticketData.resolved === "yes"
            ? "Closed"
            : ticketData.resolved === "dnc"
            ? "Done"
            : "Open",
        bgColor:
          ticketData.resolved === "yes"
            ? "#FEE2E2"
            : ticketData.resolved === "dnc"
            ? "#E8F5E9"
            : "#E3F2FD",
        textColor:
          ticketData.resolved === "yes"
            ? "#991B1B"
            : ticketData.resolved === "dnc"
            ? "#1B5E20"
            : "#0D47A1",
      },
      // Add priority object using the helper function
      priority: getPriorityInfo(ticketData.level || 1),
    };

    // Log the formatted ticket data for debugging
    console.log("Formatted ticket data:", formattedTicket);

    return { ticket: formattedTicket };
  } catch (error) {
    console.error("Error fetching ticket:", error);
    return { error: "Failed to fetch ticket data" };
  }
};

/**
 * Fetch messages for a ticket
 * @param {number} ticketId - Ticket ID to fetch messages for
 * @returns {Promise<Object>} Promise resolving to messages data and error
 */
const fetchTicketMessages = async (ticketId) => {
  try {
    // Get current user for logging
    let userData = null;
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        userData = JSON.parse(userString);
      }
    } catch (e) {
      console.warn("Error getting user data from localStorage:", e);
    }

    //console.log("Fetching messages for ticket:", ticketId);

    // From schema: ticket_messages has ticket_message_id, ticket, datetime, user, message
    const response = await api.post("/db/ticket_messages/filter", {
      filters: { ticket: parseInt(ticketId) },
      columns: ["ticket_message_id", "user", "message", "datetime"],
      order_by: "datetime",
    });

    if (!Array.isArray(response)) {
      console.error("Invalid response format for ticket messages:", response);
      return { ticketMessages: [], error: "Invalid response format" };
    }

    //console.log(`Found ${response.length} messages`);

    // Extract unique user IDs from messages
    const userIds = [...new Set(response.map((msg) => msg.user).filter(Boolean))];

    // Fetch user names if we have user IDs
    let userMap = {};
    if (userIds.length > 0) {
      try {
        const userResponse = await api.post("/db/sec_users/filter", {
          filters: { login__in: userIds },
          columns: ["login", "name"],
        });

        if (Array.isArray(userResponse)) {
          userMap = userResponse.reduce((map, user) => {
            map[user.login] = user.name;
            return map;
          }, {});
        }
      } catch (error) {
        console.warn("Error fetching user names for messages:", error);
        // Continue with original user IDs
      }
    }

    // Map messages with user names
    const formattedMessages = response.map((msg) => {
      const login = String(msg.user || "");
      const systemNames = {
        "sector-analyze": "Sector Analysis",
      };
      const systemName = systemNames[login.toLowerCase()];
      const userName = systemName
        ? systemName
        : userMap[msg.user]
          ? `${userMap[msg.user]} (${msg.user})`
          : msg.user || "System";

      return {
        ...formatMessageData(msg),
        userName,
        is_system_message: Boolean(systemName),
        is_customer_message: false,
      };
    });

    // Log viewing messages if user data is available and no previous log was made during this session
    if (userData && formattedMessages.length > 0) {
      // Only create a log entry if viewing messages for the first time
      // Use a session storage flag to track if we've already logged this view
      const viewMessagesKey = `viewed_messages_${ticketId}`;
      const hasLoggedViewMessages = sessionStorage.getItem(viewMessagesKey);

      if (!hasLoggedViewMessages) {
        await logTicketEvent({
          ticketId,
          action: "view_messages",
          details: `Viewed ${formattedMessages.length} messages`,
          user: userData.login,
        });

        // Set a flag in session storage to avoid duplicate logs
        sessionStorage.setItem(viewMessagesKey, "true");
      }
    }

    return { ticketMessages: formattedMessages, error: null };
  } catch (error) {
    console.error("Error fetching ticket messages:", error);
    return { ticketMessages: [], error: "Failed to load ticket messages" };
  }
};

/**
 * Check for higher priority tickets and older tickets that should be addressed first
 * @param {Object} currentTicket - The current ticket being viewed
 * @param {Object} userData - The current user data
 * @returns {Promise<{higherPriorityTickets: Array, olderTickets: Array}>} - Lists of tickets that should be addressed first
 */
const checkPriorityTickets = async (currentTicket, userData) => {
  try {
    if (!currentTicket || !userData) {
      console.warn("Missing required parameters for checkPriorityTickets");
      return {
        higherPriorityTickets: [],
        olderTickets: [],
      };
    }

    // First check if user is in "All" group
    const userGroupResponse = await api.post("/db/ticket_users/filter", {
      filters: {
        login: userData.login,
        ticket_group: "All",
      },
      columns: ["ticket_user_id"],
    });

    const isAllGroup = Array.isArray(userGroupResponse) && userGroupResponse.length > 0;

    // Ensure ticket_id exists and convert to number if needed
    if (!currentTicket.ticket_id) {
      console.warn("Current ticket is missing ticket_id:", currentTicket);
      if (currentTicket.id) {
        currentTicket.ticket_id = currentTicket.id;
      }
    }

    const currentLevel = parseInt(currentTicket.level);

    // Base filter for open tickets
    const baseFilter = {
      resolved: "no", // Only get unresolved tickets
      ticket_id__ne: currentTicket.ticket_id, // Not the current ticket
      department: currentTicket.department, // Always filter by department
    };

    // Fetch all relevant tickets in one call
    const ticketsResult = await api.post("/db/tickets/filter", {
      filters: baseFilter,
      columns: [
        "ticket_id",
        "ticket_code",
        "create_datetime",
        "level",
        "department",
        "current_user",
      ],
    });

    // Format tickets for display
    const formatTickets = (tickets) => {
      if (!tickets || !Array.isArray(tickets)) return [];

      return tickets.map((ticket) => ({
        id: ticket.ticket_id,
        code: ticket.ticket_code,
        createdAt: formatDbDateTime(ticket.create_datetime),
        createdAtRaw: ticket.create_datetime,
        priority: parseInt(ticket.level),
        department: ticket.department,
        assignedTo: ticket.current_user || "Unassigned",
      }));
    };

    const formattedTickets = formatTickets(ticketsResult);

    // Get higher priority tickets (lower level number = higher priority)
    const higherPriorityTickets = formattedTickets
      .filter((ticket) => ticket.priority < currentLevel)
      .sort((a, b) => {
        // Sort by priority first (ascending - lower numbers are higher priority)
        if (a.priority !== b.priority) {
          return a.priority - b.priority;
        }
        // If same priority, sort by creation date (oldest first)
        return new Date(a.createdAtRaw) - new Date(b.createdAtRaw);
      })
      .slice(0, 5); // Limit to top 5

    // Get older tickets with same priority
    const olderTickets = formattedTickets
      .filter(
        (ticket) =>
          ticket.priority === currentLevel &&
          new Date(ticket.createdAtRaw) <
            new Date(currentTicket.createDatetime || currentTicket.create_datetime)
      )
      .sort((a, b) => new Date(a.createdAtRaw) - new Date(b.createdAtRaw))
      .slice(0, 5); // Limit to top 5

    // Log for debugging
    console.log("Priority check results:", {
      isAllGroup,
      currentTicket: {
        id: currentTicket.ticket_id,
        level: currentLevel,
        createDate: currentTicket.createDatetime || currentTicket.create_datetime,
      },
      higherPriorityCount: higherPriorityTickets.length,
      olderTicketsCount: olderTickets.length,
      filters: baseFilter,
      allTickets: formattedTickets.map((t) => ({
        id: t.id,
        priority: t.priority,
        createdAt: t.createdAtRaw,
      })),
    });

    // Before final return, log the return value
    const returnValue = {
      higherPriorityTickets,
      olderTickets,
      isAllGroup,
      currentLevel,
      department: currentTicket.department,
    };
    console.log("checkPriorityTickets returning:", returnValue);
    return returnValue;
  } catch (error) {
    console.error("Error checking priority tickets:", error);
    return {
      higherPriorityTickets: [],
      olderTickets: [],
      isAllGroup: false,
      error: error.message || "Failed to check priority tickets",
    };
  }
};

/**
 * Fetch logs for a specific ticket
 * @param {number|string} ticketId - ID of the ticket to fetch logs for
 * @returns {Promise<Array>} - Array of ticket logs
 */
const fetchTicketLogs = async (ticketId) => {
  try {
    const filters = {
      ticket_id: parseInt(ticketId, 10),
    };

    const response = await api.post("/db/ticket_logs/filter", {
      filters,
      columns: ["ticket_log_id", "ticket_id", "datetime", "login", "log_message"],
      order_by: "datetime DESC",
    });

    const logs = Array.isArray(response) ? response : [];
    const logins = [
      ...new Set(logs.map((log) => String(log.login || "").trim()).filter(Boolean)),
    ];
    if (logins.length === 0) {
      return logs;
    }

    try {
      const usersResponse = await api.post("/db/sec_users/filter", {
        filters: { login__in: logins },
        columns: ["login", "name"],
      });
      const nameByLogin = {};
      (Array.isArray(usersResponse) ? usersResponse : []).forEach((user) => {
        if (user?.login) nameByLogin[user.login] = user.name || "";
      });
      return logs.map((log) => ({
        ...log,
        user_name: nameByLogin[log.login] || null,
      }));
    } catch (userError) {
      console.warn("Could not resolve ticket log user names:", userError);
      return logs;
    }
  } catch (error) {
    console.error("Error fetching ticket logs:", error);
    return [];
  }
};

/**
 * Load full ticket view payload in one request (ticket, messages, logs, priority peers).
 */
export const fetchTicketDetailView = async (ticketId, login) => {
  return api.post("/db/tickets/detail_view", {
    login,
    ticket_id: parseInt(String(ticketId), 10),
  });
};

/**
 * Map joined ticket row from detail_view to the shape used by TicketsView.
 */
export const mapDetailViewTicketRow = (ticketData, customerNameFromNav = null) => {
  if (!ticketData) return null;

  const displayCustomer =
    customerNameFromNav ||
    (ticketData.customer_code && ticketData.customer_name
      ? `[${ticketData.customer_code}] ${ticketData.customer_name}`
      : ticketData.customer_name || `Customer ID: ${ticketData.customer_id}`);

  const cu = ticketData.create_user;
  const cuName = ticketData.create_user_name;
  const createUserDisplay = cuName ? (cu ? `${cuName} (${cu})` : `${cuName}`) : cu || "-";

  const uu = ticketData.current_user;
  const uuName = ticketData.current_user_name;
  const currentUserDisplay = uuName ? (uu ? `${uuName} (${uu})` : `${uuName}`) : uu || "-";

  return {
    id: ticketData.ticket_id,
    ticket_id: ticketData.ticket_id,
    code: ticketData.ticket_code,
    customerId: ticketData.customer_id,
    customerName: displayCustomer,
    createUser: cu,
    currentUser: uu,
    createUserDisplay,
    currentUserDisplay,
    createDatetime: ticketData.create_datetime,
    updateDatetime: ticketData.update_datetime,
    resolveDatetime: ticketData.resolve_datetime,
    resolved: ticketData.resolved,
    department: ticketData.department,
    departmentLabel: getDepartmentLabel(ticketData.department),
    level: ticketData.level,
    status: {
      label:
        ticketData.resolved === "yes"
          ? "Closed"
          : ticketData.resolved === "dnc"
          ? "Done"
          : "Open",
      bgColor:
        ticketData.resolved === "yes"
          ? "#FEE2E2"
          : ticketData.resolved === "dnc"
          ? "#E8F5E9"
          : "#E3F2FD",
      textColor:
        ticketData.resolved === "yes"
          ? "#991B1B"
          : ticketData.resolved === "dnc"
          ? "#1B5E20"
          : "#0D47A1",
    },
    priority: getPriorityInfo(ticketData.level || 1),
  };
};

/**
 * Map message rows from detail_view (with user_name join) to table/chat shape.
 */
export const mapDetailViewMessages = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((msg) => {
    const login = msg.user;
    const uname = msg.user_name;
    const isSystemMessage = Boolean(msg.is_system_message);
    const userName = isSystemMessage
      ? uname || "System"
      : uname
        ? login
          ? `${uname} (${login})`
          : `${uname}`
        : login || "Unknown";
    return {
      id: msg.ticket_message_id,
      ticket_message_id: msg.ticket_message_id,
      datetime: msg.datetime,
      user: login,
      userName,
      message: msg.message,
      is_read: Boolean(msg.is_read),
      read_datetime: msg.read_datetime || null,
      read_by_login: msg.read_by_login || null,
      read_by_name: msg.read_by_name || null,
      is_customer_message: Boolean(msg.is_customer_message),
      is_system_message: isSystemMessage,
    };
  });
};

export {
  getPriorityInfo,
  getStatusInfo,
  formatTicketData,
  formatMessageData,
  fetchTicketMessages,
  checkPriorityTickets,
  fetchTicketLogs,
};
